import { isCallable } from '@afterpay/utils'
import clsx from 'clsx'
import React, { cloneElement, FunctionComponent, isValidElement, PropsWithChildren, ReactNode, SyntheticEvent } from 'react'

import { Cell, CellProps } from '../Cell'
import { AllIconNames, Icon, isPaymentIcon, PaymentIcon } from '../Icon'
import { SIZES as ICON_SIZES } from '../Icon/constants'
import { DesignLanguage, Theme, useGetSelectedTheme, useIsDesignLanguage, useThemeClass } from '../Theme'
import { Text } from '../Typography'
import { useIsBreakpointLargeAndUp } from '../useBreakpoint'
import { CARET_POSITIONS } from './constants'
import styles from './style.module.scss'

export enum Override {
  DisabledTopBorderRadius = 'disabledTopBorderRadius',
  DisabledBottomBorderRadius = 'disabledBottomBorderRadius',
  DisabledBottomBorder = 'disabledBottomBorder',
  DisabledBorderRadius = 'disabledBorderRadius',
}

type CaretPositionsType = typeof CARET_POSITIONS
type CaretPositionsKeys = keyof CaretPositionsType
export type CaretPositions = CaretPositionsType[CaretPositionsKeys]

export interface OptionProps {
  icon?: AllIconNames
  floatingHeader?: string
  onClick?: (e: SyntheticEvent) => void
  testNameSpace?: string
  ariaLabelledby?: string
  ariaLabel?: string
  override?: Override | Override[]
  disabled?: boolean
  stretch?: boolean
  component?: ReactNode
  tooltipRef?: any
  warningBorder?: boolean
  caretPosition?: CaretPositions
  children?: ReactNode
  fullWidthContent?: ReactNode | (() => ReactNode)
}

export interface BaseOptionProps extends OptionProps {
  text: string | (() => any) | JSX.Element
  changeLabel?: ReactNode | JSX.Element | void
}

interface AdditionalOptionVariantProps {
  iconRender: ReactNode
  changeLabelRender: ReactNode
  floatingHeaderRender: ReactNode
}

type OptionVariantProps = BaseOptionProps & AdditionalOptionVariantProps

export type OptionVariantComponent = FunctionComponent<OptionVariantProps>

export type OptionComponent = FunctionComponent<BaseOptionProps>

export interface HeaderWrapperProps {
  hasFullWidthContent: boolean
  children: ReactNode
}

const HeaderWrapper: FunctionComponent<HeaderWrapperProps> = ({ hasFullWidthContent, children }) => {
  if (hasFullWidthContent) {
    return <div className={styles.header}>{children}</div>
  }

  return <>{children}</>
}

interface OptionThemedWrapperProps extends PropsWithChildren {
  selectedTheme: Theme
}

const OptionThemedWrapper: FunctionComponent<OptionThemedWrapperProps> = ({ children, selectedTheme }) => {
  const mapThemeToWrapperClass: Record<Theme, string | false> = {
    [Theme.AP]: false,
    [Theme.Cash]: false,
    [Theme.Pegasus]: clsx(styles.pegasusWrapper),
    [Theme.Kelpie]: clsx(styles.kelpieWrapper),
    [Theme.KelpieMint]: clsx(styles.kelpieWrapper),
  }

  const wrapperClass = mapThemeToWrapperClass[selectedTheme]
  const hasWrapper = wrapperClass !== false
  return hasWrapper ? <div className={wrapperClass}>{children}</div> : children
}

const BaseOption: OptionVariantComponent = (props) => {
  const isBronteDesign = useIsDesignLanguage(DesignLanguage.Bronte)
  const { text, onClick, testNameSpace = '', fullWidthContent } = props
  const hasTestId = testNameSpace?.length > 0
  const hasCallback = typeof onClick !== 'undefined'
  const hasFullWidthContent = typeof fullWidthContent !== 'undefined'

  const Component = hasCallback ? 'button' : 'span'

  const { iconRender, changeLabelRender, floatingHeaderRender, ...shellOptionProps } = props

  return (
    <>
      <ShellOption {...shellOptionProps}>
        <Component>
          <HeaderWrapper hasFullWidthContent={hasFullWidthContent}>
            <div className={styles.innerContainer}>
              {isBronteDesign && floatingHeaderRender}
              <div className={styles.textAndIconWrapper}>
                {iconRender}
                <div className={styles.text}>
                  <Text size='M' renderAs='div' {...(hasTestId && { testNameSpace: `${testNameSpace}-text` })}>
                    {isCallable(text) ? text() : text}
                  </Text>
                </div>
              </div>
            </div>
            {changeLabelRender}
          </HeaderWrapper>
          {hasFullWidthContent && <div className={styles.innerContainer}> {isCallable(fullWidthContent) ? fullWidthContent() : fullWidthContent}</div>}
        </Component>
      </ShellOption>
    </>
  )
}

export const Option: OptionComponent = (props) => {
  const isBronteDesign = useIsDesignLanguage(DesignLanguage.Bronte)
  const selectedTheme = useGetSelectedTheme()
  const isPegasusTheme = selectedTheme === Theme.Pegasus
  const isKelpieTheme = selectedTheme === Theme.Kelpie || selectedTheme === Theme.KelpieMint
  const isPegasusOrKelpieTheme = isPegasusTheme || isKelpieTheme

  const { text, floatingHeader = '', icon, onClick, testNameSpace = '', disabled = false, changeLabel, fullWidthContent } = props

  const hasCallback = typeof onClick !== 'undefined'
  const hasFloatingHeader = floatingHeader.length > 0
  const hasTestId = testNameSpace?.length > 0

  const isDesktop = useIsBreakpointLargeAndUp()
  const cashThemeTextSize = isDesktop ? 'L' : 'M'

  const iconIsPaymentType = isPaymentIcon(icon)
  const iconSize = iconIsPaymentType ? ICON_SIZES.SMALL : ICON_SIZES.MEDIUM

  const renderIcon = (): ReactNode => {
    if (icon === undefined) {
      return null
    }

    const IconComponent = iconIsPaymentType ? PaymentIcon : Icon
    return (
      <div className={clsx(!isPegasusOrKelpieTheme && styles.icon)} {...(hasTestId && { 'data-testid': `${testNameSpace}-icon` })}>
        <IconComponent name={icon} size={iconSize} color={disabled ? 'Gray80' : 'Black'} />
      </div>
    )
  }

  const renderChangeLabel = (): ReactNode => {
    if (!hasCallback) {
      return null
    }

    if (typeof changeLabel !== 'undefined') {
      return changeLabel
    }

    return (
      <div className={clsx(!isPegasusOrKelpieTheme && styles.caret)} ref={props.tooltipRef}>
        <Icon.CaretRight size={isBronteDesign ? ICON_SIZES.SMALL : ICON_SIZES.EXTRA_SMALL} color={disabled ? 'Gray80' : 'Black'} />
      </div>
    )
  }

  const renderFloatingHeader = (): ReactNode => {
    if (!hasFloatingHeader) {
      return null
    }

    const defaultColor = isBronteDesign ? 'Gray40' : undefined
    const html = isBronteDesign ? 'span' : 'div'
    const bold = !isBronteDesign
    const floatingHeaderClassName = isBronteDesign ? undefined : styles.floatingHeaderWrapper
    const textSize = isBronteDesign ? 'XS' : cashThemeTextSize

    return (
      <div className={floatingHeaderClassName}>
        <Text
          size={textSize}
          color={disabled ? 'Gray60' : defaultColor}
          renderAs={html}
          bold={bold}
          {...(hasTestId && {
            testNameSpace: `${testNameSpace}-floating-header`,
          })}
        >
          {floatingHeader}
        </Text>
      </div>
    )
  }

  const renderedIcon = renderIcon()
  const renderedChangeLabel = renderChangeLabel()
  const renderedFloatingHeader = renderFloatingHeader()

  const optionVariantProps: OptionVariantProps = {
    ...props,
    iconRender: renderedIcon,
    changeLabelRender: renderedChangeLabel,
    floatingHeaderRender: renderedFloatingHeader,
  }

  const cellProps: CellProps = {
    label: text,
    body: fullWidthContent,
    asset: renderedIcon,
    accessory: renderedChangeLabel,
    ariaLabelledby: props.ariaLabelledby,
    ariaLabel: props.ariaLabel,
    testNameSpace,
    disabled,
    breakout: true,
    accessoryPosition: props.caretPosition,
    onClick: props.onClick,
  }

  const mapThemeToComponent: Record<Theme, FunctionComponent<any>> = {
    [Theme.AP]: BaseOption,
    [Theme.Cash]: BaseOption,
    [Theme.Pegasus]: Cell,
    [Theme.Kelpie]: Cell,
    [Theme.KelpieMint]: Cell,
  }

  const mapThemeToProps: Record<Theme, OptionVariantProps | CellProps> = {
    [Theme.AP]: optionVariantProps,
    [Theme.Cash]: optionVariantProps,
    [Theme.Pegasus]: cellProps,
    [Theme.Kelpie]: cellProps,
    [Theme.KelpieMint]: cellProps,
  }

  const componentProps = mapThemeToProps[selectedTheme]
  const Component = mapThemeToComponent[selectedTheme]

  return (
    <>
      {!isBronteDesign && renderedFloatingHeader}
      <OptionThemedWrapper selectedTheme={selectedTheme}>
        <Component {...componentProps} />
      </OptionThemedWrapper>
    </>
  )
}

export const ShellOption: FunctionComponent<OptionProps> = ({
  floatingHeader = '',
  onClick,
  testNameSpace = '',
  ariaLabelledby,
  ariaLabel,
  caretPosition = 'Center',
  disabled = false,
  override,
  stretch = false,
  component,
  children,
  fullWidthContent,
  warningBorder,
}) => {
  const themeClass = useThemeClass()
  const hasFloatingHeader = floatingHeader.length > 0
  const hasTestId = testNameSpace?.length > 0
  const hasCallback = typeof onClick !== 'undefined'
  const hasFullWidthContent = typeof fullWidthContent !== 'undefined'

  const overrideList = (): Override[] => {
    if (override === undefined) {
      return []
    }
    if (Array.isArray(override)) {
      return [...override]
    }
    return [override]
  }

  const handleClick = (e: SyntheticEvent): void => {
    if (typeof onClick !== 'undefined') {
      onClick(e)
    }
  }

  const overrideStyles = overrideList().map((o) => styles[o])
  const componentClassName = clsx(
    styles.button,
    hasFullWidthContent && styles.flexWrap,
    styles[`caret${caretPosition}`],
    disabled && styles.disabled,
    overrideStyles,
    hasFloatingHeader && styles.withFloatingHeader,
    hasCallback && styles.interactive,
    stretch && styles.stretch,
    warningBorder && styles.warningBorder,
    styles[`${themeClass}`]
  )

  const attributes = {
    'aria-labelledby': ariaLabelledby,
    'aria-label': ariaLabel,
    ...(hasCallback && { onClick: handleClick, role: 'button' }),
    ...(hasTestId && { 'data-testid': `${testNameSpace}-option-button` }),
  }

  if (isValidElement(component)) {
    const Component = cloneElement(component as React.ReactElement<any>, {
      className: clsx(componentClassName, styles.component, component?.props?.className),
      ...attributes,
    })
    return Component
  }

  if (isValidElement(children)) {
    const Component = cloneElement(children as React.ReactElement<any>, {
      className: clsx(componentClassName),
      ...attributes,
    })
    return Component
  }

  return null
}
